import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaHardHat, FaClipboardCheck, FaShieldAlt, FaTimes, FaBars, FaBuilding, FaUsers, FaHandshake } from 'react-icons/fa';
import { BsShieldCheck, BsGraphUp, BsClipboardCheck, BsBinoculars } from 'react-icons/bs';

// Reuse the same CSS module for a consistent look and feel
import styles from './LandingPage.module.css'; 
import imageLogo from "../../assets/images/LogoBuildStart03.png";

// IMAGE PROMPT: A wide, professional shot of a large, modern residential construction site at dawn, with several houses in various stages of completion. The mood should be optimistic and grand in scale.
import devBanner from '../../assets/images/bizbanner.png'; 

// --- Reusable Header ---
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <img src={imageLogo} alt="Build Start Logo" style={{ height: '35px', marginRight: '10px' }} />
          BUILD START
        </div>
        <nav className={`${styles.nav} ${isMenuOpen ? styles.navMobile : ''}`}>
          <ul className={styles.navLinks}>
            <li className={styles.closeIcon} onClick={toggleMenu}><FaTimes /></li>
            <li><a href="/">Home Owner</a></li>
            <li><a href="/business-landing">For Building Professionals</a></li>
            <li className={styles.activeLink} ><a href="/for-developers">For Renovators / Developers</a></li>
            <li><a href="/resources-hub">Resource Hub</a></li>
            <li><a href="/contact-support">Support</a></li>
            <li className={styles.mobileAuth}>
              <Link to="/login" className={styles.btnLogin}>Login</Link>
            </li>
            <li className={styles.mobileAuth}>
              <Link to="/select-role" className={styles.btnRegister}>Register</Link>
            </li>
          </ul>
        </nav>
        <div className={styles.headerRight}>
          <Link to="/login" className={styles.btnLogin}>Login</Link>
          <Link to="/select-role" className={styles.btnRegister}>Find Contractors</Link>
        </div>
        <div className={styles.menuIcon} onClick={toggleMenu}>
          <FaBars />
        </div>
      </div>
    </header>
  );
};

// --- Page-Specific Sections for Construction Developers ---

const DeveloperHeroSection = () => (
    <section className={styles.businessHero}>
        <div className={styles.heroImagesContainer2}>
            <img src={devBanner} alt="Large residential construction site" className={styles.heroImageBg} />
        </div>
        <div className={styles.container}>
            <div className={styles.businessHeroContent}>
                <h1>Build your team with Australia's Elite Trades Network.</h1>
                <p>Stop the endless search for reliable subcontractors. Build Start gives high-volume developers streamlined access to a pre-vetted, on-demand workforce, ensuring your projects stay on schedule and on budget.</p>
                <div className={styles.heroCtaForm}>
                    <input type="text" placeholder="What trade are you looking for?" />
                    <Link to="/select-role" className={styles.btnPrimary}>Find Your Next Subcontractor</Link>
                </div>
            </div>
        </div>
    </section>
);

const WhyBuildStartForDevelopers = () => (
    <section className={`${styles.whyBuildStart} ${styles.sectionPadding}`}>
        <div className={styles.container}>
            <h2 className={styles.sectionTitle}>A Strategic Partner in Workforce Management</h2>
            <p className={styles.subheadline}>We're more than a directory; we're your solution for building reliable project teams at scale.</p>
            <div className={styles.valueGrid}>
                <div className={styles.valueCard}>
                    <FaHardHat className={styles.valueIcon} />
                    <h3>Access Vetted Professionals</h3>
                    <p>Gain immediate access to our exclusive network of licensed, insured, and highly-rated trade professionals. We handle the vetting so you can focus on building.</p>
                </div>
                <div className={styles.valueCard}>
                    <FaClipboardCheck className={styles.valueIcon} />
                    <h3>Streamline Procurement</h3>
                    <p>Post jobs, manage bids, and award contracts through a single, efficient platform. Drastically reduce admin time and fill skill gaps faster.</p>
                </div>
                <div className={styles.valueCard}>
                    <BsGraphUp className={styles.valueIcon} />
                    <h3>Scale With Confidence</h3>
                    <p>Whether it's for a single luxury home or a 100-lot subdivision, our platform scales with your needs, providing the right trades, right when you need them.</p>
                </div>
            </div>
        </div>
    </section>
);

// This section replaces the old, unstyled one and uses a working style pattern.
const DeveloperFeaturesSection = () => (
  <section className={`${styles.sectionPadding} ${styles.lightBg}`}>
    <div className={styles.container}>
      <h2 className={styles.sectionTitle}>Tools Designed for the High-Volume Developer</h2>
      {/* IMAGE PROMPT: A clean, professional graphic displaying a mock-up of an enterprise dashboard on a tablet. The dashboard should show project timelines, contractor statuses, and budget charts. */}
      <div className={styles.valueGrid}> {/* Reusing the .valueGrid style for a consistent 3 or 4 column layout */}
        <div className={styles.valueCard}>
          <FaBuilding className={styles.valueIcon} />
          <h4>Enterprise Dashboard</h4>
          <p>Oversee all projects and subcontractor needs from a central hub. Track job statuses, manage communications, and monitor performance across your portfolio.</p>
        </div>
        <div className={styles.valueCard}>
          <FaUsers className={styles.valueIcon} />
          <h4>Job Posting</h4>
          <p>Upload your job requirements and let our system match you with qualified teams.</p>
        </div>
        <div className={styles.valueCard}>
          <FaHandshake className={styles.valueIcon} />
          <h4>Dedicated Account Manager</h4>
          <p>Receive personalised service from an expert who understands your business needs and helps you build your ideal project teams and streamline procurement.</p>
        </div>
      </div>
    </div>
  </section>
);

const BuildStartSealOfApproval = () => {
  return (
    <section className={styles.sealOfApproval}>
      <div className={`${styles.container} ${styles.textCenter}`}>
        <h2>The Build Start Seal of approval</h2>
        <p>Our 4-point Vetting Gauntlet ensures every professional meets the highest standards of skill, reputation, and integrity.</p>
        <div className={styles.sealProcess}>
          <div className={styles.sealStep}>
            <div className={styles.sealIconWrapper}>
              <span className={styles.sealNumber}>1</span>
              <BsShieldCheck size={40} />
            </div>
            <h3>License & Insurance Verification</h3>
            <p>We verify every license, certification, and insurance policy to ensure they are current and valid.</p>
          </div>
          <div className={styles.sealStep}>
            <div className={styles.sealIconWrapper}>
              <span className={styles.sealNumber}>2</span>
              <BsGraphUp size={40} />
            </div>
            <h3>Reputation & Review Analysis</h3>
            <p>Our team meticulously analyzes online reviews and requires a minimum 3-star average rating.</p>
          </div>
          <div className={styles.sealStep}>
            <div className={styles.sealIconWrapper}>
              <span className={styles.sealNumber}>3</span>
              <BsClipboardCheck size={40} />
            </div>
            <h3>Portfolio & Craftsmanship Check</h3>
            <p>We review past work for quality, attention to detail, and consistency, ensuring true mastery of their craft.</p>
          </div>
          <div className={styles.sealStep}>
            <div className={styles.sealIconWrapper}>
              <span className={styles.sealNumber}>4</span>
              <BsBinoculars size={40} />
            </div>
            <h3>Continuous Quality Monitoring</h3>
            <p>Our work isn’t done. We continuously monitor performance and reviews to ensure standards are always met.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const PartnershipPlansSection = () => (
    <section id="pricing" className={`${styles.pricingSection} ${styles.sectionPadding}`}>
        <div className={styles.container}>
             <h2 className={styles.sectionTitle}>Partnership Plans Built for Developers & Renovators</h2>
             <p className={styles.subheadline}>Flexible solutions designed to integrate with your procurement and project management workflows.</p>
             <div className={styles.pricingGrid}>
                <div className={styles.pricingCard}>
                    <h3>Pro Developer</h3>
                    <p className={styles.price}>$499<span className={styles.pricePer}>/mo</span></p>
                    <ul>
                        <li>✓ Full Directory Access</li>
                        <li>✓ Post up to 20 Jobs / Month</li>
                        <li>✓ Branded Company Profile</li>
                        <li>✓ Standard Support</li>
                    </ul>
                    <Link to="/select-role" className={styles.btnSecondary}>Get Started</Link>
                </div>
                 <div className={`${styles.pricingCard} ${styles.featuredPlan}`}>
                    <span className={styles.popularBadge}>Most Popular</span>
                    <h3>Enterprise</h3>
                    <p className={styles.price}>Custom</p>
                    <ul>
                        <li>✓ Everything in Pro Developer</li>
                        <li>✓ Unlimited Job Postings</li>
                        <li>✓ Bulk Job Upload Tool</li>
                        <li>✓ Dedicated Account Manager</li>
                        <li>✓ Advanced Reporting & Analytics</li>
                        <li>✓ Priority Support & SLAs</li>
                    </ul>
                    <Link to="/contact-support" className={styles.btnPrimary}>Request a Demo</Link>
                </div>
                 <div className={styles.pricingCard}>
                    <h3>Pay Per Hire</h3>
                    <p className={styles.price}>Flexible</p>
                    <ul>
                        <li>✓ No Subscription Fees</li>
                        <li>✓ Access Our Vetted Network</li>
                        <li>✓ Pay Only for Successful Hires</li>
                        <li>✓ Ideal for specific project needs</li>
                    </ul>
                    <Link to="/contact-support" className={styles.btnSecondary}>Contact Sales</Link>
                </div>
             </div>
        </div>
    </section>
);

const FinalCtaSection = () => (
    <section className={`${styles.finalCta} ${styles.professionals} ${styles.sectionPadding}`}>
        <div className={styles.container}>
            <h2>Don't Let Labour Shortages Slow You Down.</h2>
            <p>Partner with Build Start and gain a competitive advantage with a reliable, scalable workforce ready to build.</p>
            <Link to="/contact-support" className={styles.btnPrimaryLarge}>
                Schedule Your Free Consultation
            </Link>
        </div>
    </section>
);

// --- Main Page Component ---
const ForDevelopersPage = () => {
    return (
        <>
            <Header />
            <main>
                <DeveloperHeroSection />
                <WhyBuildStartForDevelopers />
                <DeveloperFeaturesSection />
                <PartnershipPlansSection />
                <BuildStartSealOfApproval /> 
                <FinalCtaSection />
            </main>
            {/* Remember to add your reusable Footer component here */}
        </>
    );
};

export default ForDevelopersPage;